import React, { useEffect, useState } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { emailStringValidation, passMeetsRequirements } from 'utils';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { SelectRoles } from 'components/SelectRoles/SelectRoles';
import { NewUser, Role, User, UsersApi } from 'apis/UsersApi';
import { PartnerPermissions } from 'pages/partners/types';
import { Alert, Col, Modal, Row } from 'react-bootstrap';
import { type UserPermissions } from 'pages/users/types';
import { UserDetailsContext } from 'pages/users/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useContext } from 'components/Tabs';

import 'components/NewUserForm/newUserForm.scss';

interface NewPartnerUserProps {
    customCreateClick?: (newPartnerUserObj: NewUser, permissionsCSV: string, existingUser: boolean, allowUserToSetPassword: boolean) => Promise<void>
    onCreated?: (userProfileId: number) => void;
    onExistingUserChange?: (isExistingUser: boolean) => void;
    page: 'Partner' | 'User';
    parentCancel?: () => void
    permissions: UserPermissions | PartnerPermissions;
    setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    specificPartner?: number;
}

const defaultNewUserForm: NewUser = {
    CurrentPartnerId: 0,
    CurrentUserId: 0,
    DisabledDetailsBtn: false,
    EarlyAccessPreference: 2,
    Email: '',
    FeatureRingId: 0,
    FirstName: '',
    FullName: '',
    Id: '',
    LastName: '',
    PartnerActive: true,
    PartnerInviteText: '',
    PartnerList: [],
    Password: '',
    Permissions: [],
    PhoneNumber: '',
    Profile: 'Admin',
    SelectedPartner: { Name: '', PartnerId: 0, Type: 0 },
    UserName: '',
    UserProfileID: 0
};

enum UserCreateStep {
    Details,
    Permissions,
}

export function NewUserForm({
    customCreateClick,
    onCreated,
    onExistingUserChange,
    parentCancel,
    permissions,
    setUsers,
    specificPartner
}: NewPartnerUserProps) {

    const { formatMessage } = useIntl();

    const stepItems: { label: string; step: UserCreateStep }[] = [
        { label: formatMessage({ id: 'details.7799b923', defaultMessage: 'Details' }), step: UserCreateStep.Details },
        { label: formatMessage({ id: 'permissions.076a2862', defaultMessage: 'Permissions' }), step: UserCreateStep.Permissions }
    ];

    const [savingInProcess, setSavingInProcess] = useState(false);
    const [newPartnerUserObj, setNewPartnerUserObj] = useState<NewUser>(defaultNewUserForm);
    const [userNameValid, setUserNameValid] = useState({ reason: '', valid: true });
    const [firstNameValid, setFirstNameValid] = useState(true);
    const [lastNameValid, setLastNameValid] = useState(true);
    const [passwordInput, setPasswordInput] = useState<string>('');
    const [passwordValid, setPasswordValid] = useState(true);
    const [allowUserToSetPassword, setAllowUserToSetPassword] = useState(true);
    const [newPermissionList, setNewPermissionList] = useState<string[]>([]);
    const [parentAvailableRoles, setParentAvailableRoles] = useState<Role[]>([]);
    const [step, setStep] = useState(UserCreateStep.Details);
    const { close } = useContext(UserDetailsContext);
    const [existingUserEmail, setExistingUserEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const { catchWithToast, onSuccessToast } = useNotification();

    useEffect(() => {

        onExistingUserChange?.(existingUserEmail.length > 0);

    }, [existingUserEmail]);

    const checkUserNameValid = async (username: string) => {

        const emailIsValid = emailStringValidation(username);

        if (emailIsValid) {

            const userIsValid = await UsersApi.ValidateUsername(username);

            if (userIsValid) {

                setUserNameValid({ reason: '', valid: true });

            } else {

                setExistingUserEmail(username);

                try {

                    const info = await UsersApi.GetBasicInfoByUsername(username);
                    if (info) {

                        setNewPartnerUserObj(prev => ({
                            ...prev,
                            FirstName: info.FirstName || prev.FirstName,
                            LastName: info.LastName || prev.LastName
                        }));

                    }

                } catch (e) {

                    catchWithToast(e, formatMessage({ id: 'failed_to_load_existing_user_information.954fd4ab', defaultMessage: 'Failed to load existing user information.' }));

                }

            }

        } else {

            setUserNameValid({ reason: formatMessage({ id: 'emailusername_is_not_a_valid_email_or_is.07eabd58', defaultMessage: 'Email/Username - Is not a valid email or is already taken' }), valid: false });

        }

    };

    const checkPasswordInput = (password: string) => setPasswordValid(passMeetsRequirements(password));

    const validateNewPartnerUserDetails = (justRegularJavascript: boolean) => {

        const firstNameIsValid = newPartnerUserObj.FirstName.length > 0;
        const lastNameIsValid = newPartnerUserObj.LastName.length > 0;

        if (existingUserEmail.length > 0) {

            return true;

        }

        if (!justRegularJavascript) {

            setFirstNameValid(firstNameIsValid);
            setLastNameValid(lastNameIsValid);
            if (!allowUserToSetPassword) checkPasswordInput(newPartnerUserObj.Password);

        }

        const passwordOk = allowUserToSetPassword || passwordValid;

        return userNameValid.valid && firstNameIsValid && lastNameIsValid && passwordOk;

    };

    const handleStepperChange = (nextStep: number) => {

        if (step === UserCreateStep.Details) {

            if (validateNewPartnerUserDetails(false)) setStep(UserCreateStep.Permissions);

        } else if (nextStep === UserCreateStep.Details && existingUserEmail.length === 0) {

            setStep(nextStep);

        }

    };

    const onClickCreateUser = async () => {

        setSavingInProcess(true);

        try {

            let userProfileId = 0;

            if (existingUserEmail.length > 0) {

                // Existing account: add it to the partner via Update (not Create).
                await UsersApi.Update({ ...newPartnerUserObj, Email: existingUserEmail }, newPermissionList.toString());

            } else {

                const result = await UsersApi.Create(newPartnerUserObj, newPermissionList.toString(), -1, -1, allowUserToSetPassword);
                userProfileId = result?.Id ?? 0;

            }

            const users = await UsersApi.GetAll();
            if (users) setUsers(users);

            // Update returns no id, so resolve the added account from the refreshed list.
            if (!userProfileId && existingUserEmail) {

                const email = existingUserEmail.toLowerCase();
                userProfileId = users?.find(u => (u.Email ?? u.UserName ?? '').toLowerCase() === email)?.UserProfileID ?? 0;

            }

            close(0);

            parentCancel?.();
            onSuccessToast(formatMessage({ id: 'succesfully_created_user.ca91eb24', defaultMessage: 'Succesfully Created User' }));

            if (userProfileId) onCreated?.(userProfileId);

        } catch (e) {

            setSavingInProcess(false);
            catchWithToast(e, formatMessage({ id: 'failed_to_create_user.99982644', defaultMessage: 'Failed to create user.' }));

        }

    };

    const customButton = (
        <>
            {step + 1 !== stepItems.length ? (
                <GMetrixButton disabled={existingUserEmail.length > 0} onClick={() => handleStepperChange(step + 1)} variant="primary">
                    <FormattedMessage defaultMessage="Next" description="botones" id="next.b637640c" />
                </GMetrixButton>
            ) : (
                <GMetrixButton
                    onClick={() => {

                        if (customCreateClick && specificPartner) {

                            setSavingInProcess(true);
                            const isExistingUser = existingUserEmail.length > 0;
                            const user = isExistingUser ? { ...newPartnerUserObj, Email: existingUserEmail } : newPartnerUserObj;
                            customCreateClick(user, newPermissionList.toString(), isExistingUser, allowUserToSetPassword);

                        } else {

                            onClickCreateUser();

                        }

                    }}
                    disabled={savingInProcess || !validateNewPartnerUserDetails(true) || newPermissionList.length === 0}
                    variant="primary"
                >
                    {existingUserEmail.length > 0 ? <FormattedMessage defaultMessage="Add" id="add.e3ee51bb" /> : <FormattedMessage defaultMessage="Create" id="create.be56b47d" />}
                </GMetrixButton>
            )}
        </>
    );

    return (
        <>
            <TileContainer
                addButton={parentCancel ? false : true}
                addButtonCustom={parentCancel ? undefined : customButton}
                className="p-4"
                showHr={parentCancel ? false : true}
                title={parentCancel ? undefined : formatMessage({ id: 'new_administrator.ba99468a', defaultMessage: 'New Administrator' }) || 'New Administrator'}
            >
                <GMetrixStepper
                    handleChange={e => handleStepperChange(e.value)}
                    linear={false}
                    setStep={setStep}
                    step={step}
                    stepItems={stepItems}
                />

                {/* Step is equal to Details, Permissions or Early Access. Only one can be showing */}
                {step === UserCreateStep.Details && (
                    <Row>
                        <Col className="d-flex justify-content-center" lg={6} md={6} sm={12} xl={6} xs={12}>
                            <Row className="w-100">
                                <Col className="mb-2" lg={12}>
                                    <GMetrixInput
                                        autoFocus
                                        required
                                        label={
                                            userNameValid.valid
                                                ? formatMessage({ id: 'email.5cbb5845', defaultMessage: 'Email', description: 'Formulario' })
                                                : userNameValid.reason
                                        }
                                        onChange={e => {

                                            setNewPartnerUserObj({ ...newPartnerUserObj, UserName: e.target.value });
                                            if (existingUserEmail.length > 0) setExistingUserEmail('');

                                        }}
                                        isInvalid={!userNameValid.valid}
                                        onBlur={() => checkUserNameValid(newPartnerUserObj.UserName)}
                                        value={newPartnerUserObj.UserName}
                                    />
                                    {existingUserEmail.length > 0 && (
                                        <Alert className="mt-2" variant="info">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span>
                                                    <FormattedMessage defaultMessage="The username/email" id="the_usernameemail.7e421719" />
                                                    {' '}
                                                    <b>
                                                        {existingUserEmail}
                                                    </b>
                                                    {' '}
                                                    <FormattedMessage defaultMessage="already exists. Would you like to add this user to the organization?" id="already_exists_would_you_like_to_add_thi.64aeec78" />
                                                </span>
                                                <div className="d-flex gap-2 ms-3">
                                                    <GMetrixButton
                                                        onClick={() => {

                                                            setExistingUserEmail('');
                                                            setNewPartnerUserObj({ ...newPartnerUserObj, UserName: '' });

                                                        }}
                                                        variant="outline-secondary"
                                                    >
                                                        <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                                                    </GMetrixButton>
                                                    <GMetrixButton
                                                        onClick={() => setStep(UserCreateStep.Permissions)}
                                                        variant="primary"
                                                    >
                                                        <FormattedMessage defaultMessage="Add User" id="add_user.daaf2592" />
                                                    </GMetrixButton>
                                                </div>
                                            </div>
                                        </Alert>
                                    )}
                                </Col>
                                <Col className="mb-2" lg={12}>
                                    <GMetrixInput
                                        required
                                        onBlur={() => {

                                            const firstNameIsValid = newPartnerUserObj.FirstName.length > 0;
                                            setFirstNameValid(firstNameIsValid);

                                        }}
                                        disabled={existingUserEmail.length > 0}
                                        isInvalid={!firstNameValid}
                                        label={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' })}
                                        onChange={e => setNewPartnerUserObj({ ...newPartnerUserObj, FirstName: e.target.value })}
                                        value={newPartnerUserObj.FirstName}
                                    />
                                </Col>
                                <Col className="mb-2" lg={12}>
                                    <GMetrixInput
                                        required
                                        onBlur={() => {

                                            const lastNameIsValid = newPartnerUserObj.LastName.length > 0;
                                            setLastNameValid(lastNameIsValid);

                                        }}
                                        disabled={existingUserEmail.length > 0}
                                        isInvalid={!lastNameValid}
                                        label={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' })}
                                        onChange={e => setNewPartnerUserObj({ ...newPartnerUserObj, LastName: e.target.value })}
                                        value={newPartnerUserObj.LastName}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col>
                            <Row className="w-100">
                                <Col className="mb-3" lg={12}>
                                    <GMetrixCheckInput
                                        checked={allowUserToSetPassword}
                                        disabled={existingUserEmail.length > 0}
                                        label={formatMessage({ id: 'allow_new_admin_to_set_their_own_passwor.78342e8d', defaultMessage: 'Allow new Admin to set their own password' })}
                                        onChange={e => setAllowUserToSetPassword(e.target.checked)}
                                        type="checkbox"
                                    />
                                    <small className="text-muted d-block mt-1">
                                        {allowUserToSetPassword
                                            ? formatMessage({ id: 'they_will_receive_a_welcome_email_to_set.96b033e0', defaultMessage: 'They will receive a welcome email to set their own password, then be signed in automatically.' })
                                            : formatMessage({ id: 'you_set_a_password_to_share_with_them_di.66ebee32', defaultMessage: 'You set a password to share with them directly. They can sign in with it right away.' })}
                                    </small>
                                </Col>
                                {!allowUserToSetPassword && (
                                    <>
                                        <Col className={`mb-2 new-user-password-col${!passwordValid ? ' is-invalid' : ''}`} lg={12}>
                                            <GMetrixInput
                                                required
                                                label={
                                                    passwordValid
                                                        ? formatMessage({ id: 'password.eb68d5ea', defaultMessage: 'Password', description: 'Formulario' })
                                                        : formatMessage({ id: 'password_please_refer_to_password_requir.a94d07a4', defaultMessage: 'Password - Please refer to password requirements' })
                                                }
                                                onBlur={() => {

                                                    checkPasswordInput(passwordInput);

                                                }}
                                                onChange={e => {

                                                    setNewPartnerUserObj({ ...newPartnerUserObj, Password: e.target.value });
                                                    setPasswordInput(e.target.value);

                                                }}
                                                disabled={existingUserEmail.length > 0}
                                                isInvalid={!passwordValid}
                                                name="password"
                                                type={showPassword ? 'text' : 'password'}
                                                value={newPartnerUserObj.Password}
                                            />
                                            <button
                                                className="new-user-eye-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                                tabIndex={-1}
                                                type="button"
                                            >
                                                <i className={`fa-light fa-lg ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                                            </button>
                                        </Col>
                                        <Col>
                                            <b>
                                                <FormattedMessage defaultMessage="Password requirements:" id="password_requirements.6aaa2f45" />
                                            </b>
                                            {' '}
                                            <FormattedMessage defaultMessage="minimum 8 characters, at least one uppercase letter, one lowercase, one number, and one special character." id="minimum_8_characters_at_least_one_upperc.2ba9707a" />
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Col>
                    </Row>
                )}
                {step === UserCreateStep.Permissions && (
                    <SelectRoles
                        handlerType="Create"
                        newPermissionList={newPermissionList}
                        parentAvailableRoles={parentAvailableRoles}
                        partnerId={undefined}
                        partnerType={specificPartner}
                        permissions={permissions}
                        setNewPermissionList={setNewPermissionList}
                        setParentAvailableRoles={setParentAvailableRoles}
                        user={newPartnerUserObj}
                    />
                )}

            </TileContainer>
            {parentCancel && (
                <Modal.Footer
                    className="border-0 p-0"
                >
                    <div className="d-md-flex align-items-center justify-content-end">
                        <GMetrixButton
                            className="me-3"
                            onClick={parentCancel}
                            variant="outline-secondary"
                        >
                            <FormattedMessage defaultMessage="Cancel" description="botones" id="cancel.5b254f8e" />
                        </GMetrixButton>
                        {customButton}
                    </div>
                </Modal.Footer>
            )}
        </>
    );

}
